import React from 'react';
import {
  Box,
  Button,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssignmentReturn as RefundIcon,
  Cancel as CancelIcon,
  Gavel as TermsIcon,
  Security as PrivacyIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { PageShell } from '../StaticPages';

const BILLING_HOME = '/billing';

interface BillingLegalPageProps {
  icon: React.ReactElement;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

const BillingLegalPage: React.FC<BillingLegalPageProps> = ({
  icon,
  title,
  subtitle,
  children,
}) => (
  <PageShell icon={icon} title={title} subtitle={subtitle}>
    {children}
    <Box sx={{ mt: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        <Button component={RouterLink} to={BILLING_HOME} variant="contained">
          Back to billing
        </Button>
        <Button component={RouterLink} to="/billing/terms-and-conditions" variant="outlined" size="small">
          Terms
        </Button>
        <Button component={RouterLink} to="/billing/privacy-policy" variant="outlined" size="small">
          Privacy
        </Button>
        <Button component={RouterLink} to="/billing/refund-policy" variant="outlined" size="small">
          Refunds
        </Button>
        <Button component={RouterLink} to="/billing/cancellation-policy" variant="outlined" size="small">
          Cancellation
        </Button>
      </Stack>
    </Box>
  </PageShell>
);

export const BillingTermsAndConditionsPage: React.FC = () => (
  <BillingLegalPage
    icon={<TermsIcon color="primary" fontSize="large" />}
    title="Terms and Conditions"
    subtitle="Billing, subscriptions, and payment terms for Sustainow Carbon Intelligence."
  >
    <Typography paragraph>
      These terms apply when you purchase or renew a paid plan, pay usage-based fees, or otherwise
      complete checkout on the billing page. By proceeding with payment, you confirm that you are
      authorized to bind your organization and that billing details (legal name, GSTIN if applicable,
      and contact information) are accurate.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Plans and pricing
    </Typography>
    <Typography paragraph>
      Fixed plans and usage-based quotes are shown in INR before checkout. Taxes (including GST, if
      applicable) may be added on the invoice or payment receipt as required by law. Administrators
      may enable or disable billing modules and payment methods; displayed quotes are informational
      until checkout is completed successfully.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Payments
    </Typography>
    <Typography paragraph>
      Payments are processed through our payment partner (for example, Razorpay) using UPI, net
      banking, or cards when enabled. You agree not to initiate chargebacks or disputes in bad faith.
      Failed or incomplete payments do not create an active paid entitlement until verification
      succeeds.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Platform access
    </Typography>
    <Typography paragraph>
      Unless your organization configures stricter billing policy, platform features are not
      automatically restricted when a payment is pending or missed. Billing history is maintained for
      reference, reporting, and administrator review.
    </Typography>
    <Typography paragraph>
      For billing disputes or contract questions, contact <strong>contact@sustainow.in</strong>.
    </Typography>
  </BillingLegalPage>
);

export const BillingPrivacyPolicyPage: React.FC = () => (
  <BillingLegalPage
    icon={<PrivacyIcon color="primary" fontSize="large" />}
    title="Privacy Policy (Billing)"
    subtitle="How payment and billing-related data is collected and used."
  >
    <Typography paragraph>
      This notice supplements our general privacy policy and applies to checkout, invoices, and
      payment history on the billing page.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Data we collect
    </Typography>
    <List dense>
      <ListItem>
        <ListItemText
          primary="Account and organization identifiers"
          secondary="Links payments to your MSME profile and administrator configuration."
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary="Transaction metadata"
          secondary="Order IDs, amounts, currency (INR), plan or usage model, timestamps, and payment status."
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary="Payment partner data"
          secondary="Our processor may collect card, UPI, or bank details according to their privacy policy; we do not store full card numbers on Sustainow servers."
        />
      </ListItem>
    </List>
    <Typography variant="h6" gutterBottom>
      How we use billing data
    </Typography>
    <Typography paragraph>
      We use billing data to complete checkout, verify payments, display payment history, prevent
      fraud, comply with tax and accounting obligations, and improve billing reliability. We do not
      sell billing data to third parties.
    </Typography>
    <Typography paragraph>
      For access, correction, or deletion requests related to billing records, contact{' '}
      <strong>contact@sustainow.in</strong>. You may also review the general{' '}
      <Link component={RouterLink} to="/privacy-policy">
        platform privacy policy
      </Link>
      .
    </Typography>
  </BillingLegalPage>
);

export const BillingRefundPolicyPage: React.FC = () => (
  <BillingLegalPage
    icon={<RefundIcon color="primary" fontSize="large" />}
    title="Refund Policy"
    subtitle="When refunds may be issued for Sustainow plan and usage payments."
  >
    <Typography paragraph>
      We want billing to be predictable. Refunds are handled in line with applicable Indian consumer
      and commercial law and the nature of the service delivered.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Eligible refunds
    </Typography>
    <List dense>
      <ListItem>
        <ListItemText
          primary="Duplicate or erroneous charges"
          secondary="Confirmed duplicate payments or clear processing errors are refunded to the original payment method where possible."
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary="Failed service activation"
          secondary="If payment succeeded but your entitled plan was not recorded due to a platform error, contact support within 14 days."
        />
      </ListItem>
    </List>
    <Typography variant="h6" gutterBottom>
      Generally non-refundable
    </Typography>
    <Typography paragraph>
      Fees for periods already consumed, usage-based charges calculated from activity data, or
      payments made more than 30 days before a refund request may not qualify unless required by law
      or agreed in writing with your organization.
    </Typography>
    <Typography variant="h6" gutterBottom>
      How to request a refund
    </Typography>
    <Typography paragraph>
      Email <strong>contact@sustainow.in</strong> with your organization name, payment date, amount,
      and Razorpay order or payment ID. We typically respond within 5–7 business days. Approved
      refunds are initiated through the payment partner and may take additional banking days to
      appear in your account.
    </Typography>
  </BillingLegalPage>
);

export const BillingCancellationPolicyPage: React.FC = () => (
  <BillingLegalPage
    icon={<CancelIcon color="primary" fontSize="large" />}
    title="Cancellation Policy"
    subtitle="How to stop renewals or change your billing arrangement."
  >
    <Typography paragraph>
      You may change plans, switch between fixed and usage-based models (when enabled), or stop
      future charges according to the options available on the billing page and your administrator
      settings.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Fixed plans
    </Typography>
    <Typography paragraph>
      Monthly and annual plans remain active through the paid period unless your administrator
      disables billing or you request cancellation before the next renewal. Cancellation stops
      automatic renewal for subsequent periods; it does not retroactively refund the current period
      except as described in the refund policy.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Usage-based billing
    </Typography>
    <Typography paragraph>
      Usage quotes reflect activity in the billing window. To avoid future usage-based checkout,
      switch to a fixed plan or contact your administrator to adjust billing configuration.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Effect on platform access
    </Typography>
    <Typography paragraph>
      Cancelling a plan or declining renewal does not automatically delete your data. Unless your
      organization applies a stricter policy, core platform access may continue for operational and
      reporting needs; confirm expectations with your account administrator.
    </Typography>
    <Typography paragraph>
      To request cancellation or downgrade assistance, contact <strong>contact@sustainow.in</strong>.
    </Typography>
  </BillingLegalPage>
);

export const BILLING_LEGAL_LINKS = [
  { label: 'Terms and Conditions', path: '/billing/terms-and-conditions' },
  { label: 'Privacy Policy', path: '/billing/privacy-policy' },
  { label: 'Refund Policy', path: '/billing/refund-policy' },
  { label: 'Cancellation Policy', path: '/billing/cancellation-policy' },
] as const;
